import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import './ProblemsTab.css';

export interface Problem {
  id: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  file: string;
  line: number;
  column: number;
}

interface ProblemsTabProps {
  problems: Problem[];
  onProblemClick?: (problem: Problem) => void;
}

const ProblemsTab: React.FC<ProblemsTabProps> = ({ problems, onProblemClick }) => {
  const { t } = useTranslation();

  const errorCount = problems.filter(p => p.severity === 'error').length;
  const warningCount = problems.filter(p => p.severity === 'warning').length;
  const infoCount = problems.filter(p => p.severity === 'info').length;

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error': return <FaTimes size={12} />;
      case 'warning': return <FaExclamationTriangle size={12} />;
      case 'info': return <FaInfoCircle size={12} />;
      default: return '•';
    }
  };

  return (
    <div className="problems-tab-container">
      <div className="problems-tab-header">
        <div className="problems-summary">
          {errorCount > 0 && (
            <span className="problems-count error">
              <FaTimes size={12} /> {errorCount}
            </span>
          )}
          {warningCount > 0 && (
            <span className="problems-count warning">
              <FaExclamationTriangle size={12} /> {warningCount}
            </span>
          )}
          {infoCount > 0 && (
            <span className="problems-count info">
              <FaInfoCircle size={12} /> {infoCount}
            </span>
          )}
        </div>
      </div>
      <div className="problems-tab-content">
        {problems.length === 0 ? (
          <div className="problems-empty">
            {t('problems.noProblems', 'No problems detected in workspace.')}
          </div>
        ) : (
          <div className="problems-list">
            {problems.map((problem) => (
              <div 
                key={problem.id} 
                className={`problem-item ${problem.severity}`}
                onClick={() => onProblemClick?.(problem)}
              >
                <span className="problem-icon">{getSeverityIcon(problem.severity)}</span>
                <div className="problem-details">
                  <div className="problem-message">{problem.message}</div>
                  <div className="problem-location">
                    {problem.file} [{problem.line}, {problem.column}]
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemsTab;
